import { KdsModal, KdsText } from "@kazaar-platform/design-system";
import { useTranslate } from "@tolgee/react";
import { useOrderItems, useToasts } from "@/hooks";
import { type ModalCancelOrderItemProps } from "./ModalCancelOrderItem.types";

export const ModalCancelOrderItem = ({
  handleErrorOpenChange,
  isOpen,
  order,
  orderItem,
  onOpenChange,
}: ModalCancelOrderItemProps) => {
  const { t } = useTranslate();
  const { add } = useToasts();

  const { useCancelOrderItem } = useOrderItems();
  const { mutateAsync: cancelOrderItem, isPending: isCancellingOrderItem } =
    useCancelOrderItem();

  const handleCancelOrderItem = async () => {
    if (!orderItem) {
      return;
    }

    try {
      const res = await cancelOrderItem({
        orderId: order.id,
        orderItemId: orderItem.id,
      });

      if (res.status === 200 || res.status === 201) {
        onOpenChange(false);
        add({
          state: "success",
          title: t("cardOrder.toast.successCancel"),
        });
      }
    } catch {
      handleErrorOpenChange(true);
      add({
        state: "error",
        title: t("cardOrder.toast.errorCancel"),
      });
    } finally {
      onOpenChange(false);
    }
  };

  return (
    <KdsModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size="sm"
      dialogProps={{
        dialogHeaderProps: {
          action: () => onOpenChange(false),
          title: t("cardOrder.modalCancelOrderItem.title", {
            name: orderItem.sandboxItem?.catalogMedium?.name,
          }),
        },
        content: (
          <KdsText>
            {t("cardOrder.modalCancelOrderItem.subtitle", {
              name: orderItem.sandboxItem?.catalogMedium?.name,
            })}
          </KdsText>
        ),
        dialogFooterProps: {
          buttonProps: [
            {
              children: t("buttons.cancel"),
              variant: "ghost",
              color: "neutral",
              isLoading: isCancellingOrderItem,
              disabled: isCancellingOrderItem,
              onPress: () => onOpenChange(false),
            },
            {
              children: t("buttons.cancelOrder"),
              color: "error",
              isLoading: isCancellingOrderItem,
              disabled: isCancellingOrderItem,
              onPress: handleCancelOrderItem,
            },
          ],
          type: "double",
        },
      }}
    />
  );
};
